// VerificationFormPage — PUBLIC previous-landlord verification form (/verify/:token).
// A fully client-side branching interview (loads once, instant steps, one submit):
// exit ramps (never-rented / decline), an early relationship shortcut, then a
// confirm-tenancy step and screening questions phrased for current vs former.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  getVerification,
  submitVerification,
  type VerificationScreening,
  type VerificationSubmissionBody,
  type VerificationView, authorizationUrl } from '@/lib/verificationClient';

// The office line — one shared constant (WEL-171: 509-525-1040 everywhere).
import { OFFICE_PHONE } from '@/lib/officeContact';

type Step = 'rented' | 'relationship' | 'tenancy' | 'screening' | 'respondent' | 'review';

function addressLine(a: VerificationView['address']): string {
  const cs = a.city && a.state ? `${a.city}, ${a.state}` : a.city || a.state || '';
  return [a.line1, a.line2, cs, a.postalCode].filter(Boolean).join(', ');
}

function tenantLabel(names: string[]): string {
  if (!names.length) return 'the applicant';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function YesNo(props: { label: string; value: boolean | undefined; onChange: (v: boolean) => void }) {
  return (
    <div className="field">
      <span className="label">{props.label}</span>
      <div className="cluster" style={{ gap: 'var(--space-5)' }}>
        <label style={{ cursor: 'pointer' }}>
          <input type="radio" checked={props.value === false} onChange={() => props.onChange(false)} /> No
        </label>
        <label style={{ cursor: 'pointer' }}>
          <input type="radio" checked={props.value === true} onChange={() => props.onChange(true)} /> Yes
        </label>
      </div>
    </div>
  );
}

function Detail(props: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="field">
      <span className="label">{props.label}</span>
      <textarea className="input" rows={2} value={props.value} onChange={(e) => props.onChange(e.target.value)} />
    </div>
  );
}

export function VerificationFormPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [view, setView] = useState<VerificationView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<Step>('rented');

  // Draft answers (strings for inputs; converted on submit).
  const [related, setRelated] = useState<boolean | undefined>(undefined);
  const [relationshipExplain, setRelationshipExplain] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [stillResiding, setStillResiding] = useState<boolean | undefined>(undefined);
  const [moveIn, setMoveIn] = useState('');
  const [moveOut, setMoveOut] = useState('');
  const [monthlyRent, setMonthlyRent] = useState('');
  const [sc, setSc] = useState<VerificationScreening>({});
  const [resp, setResp] = useState({ name: '', email: '', phone: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getVerification(token);
        if (!cancelled) {
          setView(v);
          if (v.claimed.residedFrom) setMoveIn(v.claimed.residedFrom);
          if (v.claimed.residedTo) setMoveOut(v.claimed.residedTo);
          if (v.claimed.monthlyRent !== undefined) setMonthlyRent(String(v.claimed.monthlyRent));
          if (v.current) setStillResiding(true);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const updateSc = useCallback(
    <K extends keyof VerificationScreening>(k: K, val: VerificationScreening[K]) =>
      setSc((prev) => ({ ...prev, [k]: val })),
    [],
  );

  const send = useCallback(
    async (body: VerificationSubmissionBody) => {
      setBusy(true);
      setError(null);
      try {
        const v = await submitVerification(token, body);
        setView(v);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Submission failed');
      } finally {
        setBusy(false);
      }
    },
    [token],
  );

  const tenants = useMemo(() => (view ? tenantLabel(view.tenantNames) : ''), [view]);

  if (loading) return <Shell><p>Loading…</p></Shell>;
  if (!view) return <Shell><p>{error ?? 'This link is no longer available.'}</p></Shell>;

  // WEL-46: staff can cancel an outstanding request — the public GET surfaces a
  // terminal 'cancelled' state (not in the base VerificationState union, so we
  // compare via a widened string). Show a calm "no action needed" card matching
  // the other closed states above; never a fillable form.
  if ((view.state as string) === 'cancelled')
    return (
      <Shell>
        <h1 className="card-title">This request has been canceled</h1>
        <p>This rental verification request was canceled — no action is needed on your part.</p>
        <p style={{ color: 'var(--fg-muted)' }}>
          If you have questions, contact our office at {OFFICE_PHONE}.
        </p>
      </Shell>
    );
  if (view.state === 'submitted')
    return (
      <Shell>
        <h1 className="card-title">Thank you</h1>
        <p>Your response has been recorded. There's nothing more to do.</p>
        <p style={{ color: 'var(--fg-muted)' }}>
          Need to add context? Call our office at {OFFICE_PHONE}.
        </p>
      </Shell>
    );
  if (view.state === 'expired')
    return (
      <Shell>
        <h1 className="card-title">This link has expired</h1>
        <p>Please contact our office at {OFFICE_PHONE} if you'd still like to provide a reference.</p>
      </Shell>
    );
  if (view.state === 'unavailable')
    return (
      <Shell>
        <h1 className="card-title">This link is no longer available</h1>
        <p>If you believe this is an error, contact our office at {OFFICE_PHONE}.</p>
      </Shell>
    );

  const addr = addressLine(view.address);
  const former = !view.current;

  return (
    <Shell>
      <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>Rental history verification · Welcome Home Properties</p>
      <h1 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>
        {tenants}
      </h1>
      <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>{addr}</p>
      {view.hasAuthorization && (
        // First thing on the page, before any question (Miles, 2026-09-02):
        // professional property managers want to READ the applicant's signed
        // authorization before answering anything — Windermere refused a
        // verification until they saw it. Opens the PDF in a new tab; the
        // token in the URL is the same credential this page loaded with.
        <p style={{ marginTop: 0 }}>
          <a
            className="btn"
            data-variant="secondary"
            href={authorizationUrl(token)}
            target="_blank"
            rel="noreferrer"
          >
            View the applicant&rsquo;s signed authorization
          </a>
        </p>
      )}
      {error && <div className="alert" data-tone="danger"><div className="alert-body">{error}</div></div>}

      {step === 'rented' && (
        <div className="stack">
          <p>Did you rent to <strong>{tenants}</strong> at this address?</p>
          <div className="cluster">
            <button className="btn" data-variant="secondary" disabled={busy} onClick={() => send({ neverRented: true })}>
              I never rented to this person
            </button>
            <button className="btn" data-variant="primary" onClick={() => setStep('relationship')}>
              Yes, I did
            </button>
          </div>
          <details>
            <summary style={{ cursor: 'pointer', color: 'var(--fg-muted)' }}>I'd rather not participate</summary>
            <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
              <Detail label="Reason (optional)" value={declineReason} onChange={setDeclineReason} />
              <button className="btn" data-variant="ghost" disabled={busy} onClick={() => void send(declineReason ? { declined: true, declineReason } : { declined: true })}>
                Decline this request
              </button>
            </div>
          </details>
        </div>
      )}

      {step === 'relationship' && (
        <div className="stack">
          <YesNo label={`Are you related to, or a friend or coworker of, ${tenants}?`} value={related} onChange={setRelated} />
          {related === true && (
            <Detail label="Please explain" value={relationshipExplain} onChange={setRelationshipExplain} />
          )}
          <div className="cluster">
            <button className="btn" data-variant="ghost" onClick={() => setStep('rented')}>Back</button>
            {related === true ? (
              <button className="btn" data-variant="primary" disabled={busy} onClick={() => void send(relationshipExplain ? { related: true, relationshipExplain } : { related: true })}>
                Submit
              </button>
            ) : (
              <button className="btn" data-variant="primary" disabled={related === undefined} onClick={() => setStep('tenancy')}>
                Continue
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'tenancy' && (
        <div className="stack">
          <p>Please confirm or correct the tenancy details:</p>
          {view.current && <YesNo label="Is this person still residing there?" value={stillResiding} onChange={setStillResiding} />}
          <div className="field"><span className="label">Move-in</span><input className="input" value={moveIn} onChange={(e) => setMoveIn(e.target.value)} /></div>
          {!stillResiding && <div className="field"><span className="label">Move-out</span><input className="input" value={moveOut} onChange={(e) => setMoveOut(e.target.value)} /></div>}
          <div className="field"><span className="label">Monthly rent ($)</span><input className="input" inputMode="decimal" value={monthlyRent} onChange={(e) => setMonthlyRent(e.target.value)} /></div>
          <div className="cluster">
            <button className="btn" data-variant="ghost" onClick={() => setStep('relationship')}>Back</button>
            <button className="btn" data-variant="primary" onClick={() => setStep('screening')}>Continue</button>
          </div>
        </div>
      )}

      {step === 'screening' && (
        <div className="stack">
          <div className="field"><span className="label">How many other people {former ? 'were' : 'are'} living there with them?</span>
            <input className="input" inputMode="numeric" value={sc.othersCount ?? ''} onChange={(e) => updateSc('othersCount', e.target.value ? Number(e.target.value) : undefined)} /></div>

          {former && (
            <>
              <YesNo label="Did they give proper notice to vacate?" value={sc.properNotice} onChange={(v) => updateSc('properNotice', v)} />
              {sc.properNotice === true && <Detail label="What were the vacate terms?" value={sc.properNoticeTerms ?? ''} onChange={(v) => updateSc('properNoticeTerms', v)} />}
            </>
          )}

          <YesNo label={former ? 'Were they ever evicted?' : 'Are they currently under eviction?'} value={sc.eviction} onChange={(v) => updateSc('eviction', v)} />
          {sc.eviction === true && <Detail label="Please explain" value={sc.evictionExplain ?? ''} onChange={(v) => updateSc('evictionExplain', v)} />}

          <YesNo label="Any NSF checks or late payments?" value={sc.nsfOrLate} onChange={(v) => updateSc('nsfOrLate', v)} />
          {sc.nsfOrLate === true && <Detail label="How many? How late?" value={sc.nsfLateDetail ?? ''} onChange={(v) => updateSc('nsfLateDetail', v)} />}

          {former && (
            <>
              <YesNo label="Any outstanding debt owed?" value={sc.outstandingDebt} onChange={(v) => updateSc('outstandingDebt', v)} />
              {sc.outstandingDebt === true && <Detail label="How much, and for what?" value={sc.debtDetail ?? ''} onChange={(v) => updateSc('debtDetail', v)} />}
            </>
          )}

          <YesNo label="Any lease violations?" value={sc.leaseViolations} onChange={(v) => updateSc('leaseViolations', v)} />
          {sc.leaseViolations === true && <Detail label="Please explain" value={sc.violationsExplain ?? ''} onChange={(v) => updateSc('violationsExplain', v)} />}

          <YesNo label="Any animals?" value={sc.pets} onChange={(v) => updateSc('pets', v)} />
          {sc.pets === true && <Detail label="What kind and how many?" value={sc.petsDetail ?? ''} onChange={(v) => updateSc('petsDetail', v)} />}

          <YesNo label="Any other problems or concerns?" value={sc.otherConcerns} onChange={(v) => updateSc('otherConcerns', v)} />
          {sc.otherConcerns === true && <Detail label="Please explain" value={sc.otherConcernsDetail ?? ''} onChange={(v) => updateSc('otherConcernsDetail', v)} />}

          <YesNo label="Would you rent to them again?" value={sc.rentAgain} onChange={(v) => updateSc('rentAgain', v)} />
          {sc.rentAgain === false && <Detail label="Why not?" value={sc.rentAgainWhyNot ?? ''} onChange={(v) => updateSc('rentAgainWhyNot', v)} />}

          <div className="cluster">
            <button className="btn" data-variant="ghost" onClick={() => setStep('tenancy')}>Back</button>
            <button className="btn" data-variant="primary" onClick={() => setStep('respondent')}>Continue</button>
          </div>
        </div>
      )}

      {step === 'respondent' && (
        <div className="stack">
          <p>Finally, please confirm your contact information:</p>
          <div className="field"><span className="label">Your name</span><input className="input" value={resp.name} onChange={(e) => setResp({ ...resp, name: e.target.value })} /></div>
          <div className="field"><span className="label">Email</span><input className="input" value={resp.email} onChange={(e) => setResp({ ...resp, email: e.target.value })} /></div>
          <div className="field"><span className="label">Phone</span><input className="input" value={resp.phone} onChange={(e) => setResp({ ...resp, phone: e.target.value })} /></div>
          <div className="cluster">
            <button className="btn" data-variant="ghost" onClick={() => setStep('screening')}>Back</button>
            <button
              className="btn"
              data-variant="primary"
              disabled={busy}
              onClick={() => {
                const confirmed: NonNullable<VerificationSubmissionBody['confirmed']> = {};
                if (view.current && stillResiding !== undefined) confirmed.stillResiding = stillResiding;
                if (moveIn) confirmed.moveIn = moveIn;
                if (!stillResiding && moveOut) confirmed.moveOut = moveOut;
                if (monthlyRent) confirmed.monthlyRent = Number(monthlyRent);
                const respondent: NonNullable<VerificationSubmissionBody['respondent']> = {};
                if (resp.name) respondent.name = resp.name;
                if (resp.email) respondent.email = resp.email;
                if (resp.phone) respondent.phone = resp.phone;
                void send({ related: false, confirmed, screening: sc, respondent });
              }}
            >
              {busy ? 'Submitting…' : 'Confirm and submit'}
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--fg)', fontFamily: 'var(--font-sans)', padding: 'var(--space-6)' }}>
      <div className="card" style={{ maxWidth: 560, margin: '0 auto', padding: 'var(--space-6)' }}>{children}</div>
    </div>
  );
}
